import { Divider, Form } from 'antd';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import ButtonsToGenerateReports from '@/components/Reports/ButtonsToGenerateReports';
import { DefaultRouteTabs } from '@/components/_common';
import { PageProps } from '@/types/PageProps';
import FinancialDreForm from '@/components/Reports/FinancialReports/FinancialDreForm';
import { useReportGenerate } from '@/hooks';
import {
  useQuestReportDRE,
  useQuestReportDREAccrual,
} from '@/core/domain/questReport/Reports/DRE/DRE.hooks';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import { ReportCallback } from '@/hooks/useReportGenerate';
import {
  DREFormValues,
  DREQuestReportType,
  QuestDREReportRequest,
} from '@/core/domain/questReport/Reports/DRE/DRE.type';
import { DateTimeFormat } from '@/utils/moment';

function FinancialDrePage({ route }: PageProps) {
  const [form] = Form.useForm<DREFormValues>();
  const intl = useIntl();

  const { setFileLoading, setFileUnloading, fileLoading } = useFileLoading();

  const questReportDRE = useQuestReportDRE();
  const questReportDREAccrual = useQuestReportDREAccrual();

  const report = useReportGenerate();

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);
    try {
      const {
        initialDate,
        finalDate,
        companiesIds,
        planAccountIds,
        ...values
      } = await form.validateFields();

      const obj: QuestDREReportRequest = {
        ...values,
        reportResponseType: format,
        companiesIds: companiesIds && companiesIds.toString(),
        planAccountIds: planAccountIds && planAccountIds.toString(),
        startDate: initialDate.format(DateTimeFormat),
        endDate: finalDate.format(DateTimeFormat),
      };

      const result =
        values.dreQuestReportType === DREQuestReportType.default
          ? await questReportDRE.mutateAsync(obj)
          : await questReportDREAccrual.mutateAsync(obj);
      if (callback) {
        callback(result);
      }
    } catch {}

    setFileUnloading(format);
  };

  const handleView = () => {
    handleGenerateReport('REPORT_VIEW', (result) =>
      report.openFile({
        result,
        fileName: 'relatorio_demonstrativo_resultado.pdf',
        fileType: 'application/pdf',
      }),
    );
  };

  const handlePdf = () => {
    handleGenerateReport('PDF', (result) =>
      report.downloadFile({
        result,
        fileName: 'relatorio_demonstrativo_resultado.pdf',
      }),
    );
  };

  const handleCsv = () => {
    handleGenerateReport('CSV', (result) =>
      report.downloadFile({
        result,
        fileName: 'relatorio_demonstrativo_resultado.csv',
      }),
    );
  };

  return (
    <DefaultRouteTabs route={route.parent} permissions={route.permissions}>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <FinancialDreForm form={form} />

      <Divider />

      <ButtonsToGenerateReports
        viewReport={handleView}
        generatePdf={handlePdf}
        generateCsv={handleCsv}
        loading={fileLoading}
      />
    </DefaultRouteTabs>
  );
}
export default FinancialDrePage;
